import React, { useMemo, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import { useQuery } from '@tanstack/react-query';
import LimitationCard from 'src/components/pages/placementDetails/subpages/restrictions/limitationCard';
import RestrictionCard from 'src/components/pages/placementDetails/subpages/restrictions/restrictionCard';
import TextLink from 'src/components/shared/textLink/textLink';
import { restrictionsHaveError } from 'src/hooks/restrictionValidation/restrictionsValidator';
import { useFetchTaskGroups } from 'src/hooks/restrictionValidation/useFetchTaskGroups';
import { getAccommodationQueryOptions } from 'src/services/accommodation/accommodationQueries';
import { useAppSelector } from 'src/store/store';
import { removeSpecialCharactersFromString } from 'src/utils/helpers';
import { JmrFlowTypes } from 'src/utils/jmrUtils';

import ConfirmUpdateRestrictionsModal from '../../confirmUpdateRestrictionsModal/confirmUpdateRestrictionsModal';

/**
 * Default component.
 */
const Restrictions = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { featureFlags, isFeatureFlagsLoaded } = useAppSelector((state) => state.user);
  const [isRestrictionEntriesErrorModalOpen, setIsRestrictionEntriesErrorModalOpen] = useState(false);
  const { placementId } = useParams();
  const { data: placementDetails, isSuccess: isPlacementSuccess } = useQuery(
    getAccommodationQueryOptions.accommodationDetails(+placementId!),
  );
  const { taskGroups, isLoading: isTaskGroupsLoading } = useFetchTaskGroups(
    isFeatureFlagsLoaded,
    featureFlags,
    placementDetails?.regionId,
  );
  const nonPhysicalLimitationsEnabled =
    isPlacementSuccess && featureFlags.NON_PHYSICAL_LIMITATIONS && placementDetails.site?.nonPhysicalLimitationsEnabled;
  const mustUpdateRestrictions = useMemo(() => {
    return restrictionsHaveError(placementDetails?.workRestrictions, placementDetails?.modifiedShiftLength, taskGroups);
  }, [placementDetails?.workRestrictions, placementDetails?.modifiedShiftLength, taskGroups]);

  const updateRestrictions = () => {
    navigate(`/newJMR/${placementId}/update/${JmrFlowTypes.NewRestrictions}`, {
      state: { returnTo: location.pathname },
    });
  };

  return isPlacementSuccess ? (
    <>
      <Column width="fill" spacing="400">
        {/* Display "Restriction Has Errors" alert */}
        {!isTaskGroupsLoading && mustUpdateRestrictions && (
          <Alert type="error" width="100%">
            Please update this case's work restrictions using our new entry process.
            <TextLink onClick={() => setIsRestrictionEntriesErrorModalOpen(true)}>Update restrictions</TextLink>
          </Alert>
        )}

        {placementDetails.workRestrictions.map((restriction) => (
          <RestrictionCard
            key={restriction.taskId}
            taskName={restriction.taskName}
            endAt={placementDetails.endAtDate}
            hoursRestricted={restriction.restrictedValue}
            bodyLocation={restriction.bodyLocation}
            yesNoTypeTask={restriction.yesNoTypeTask}
            dataCy={`placement-details__restrictions__task-${removeSpecialCharactersFromString(restriction.taskName)}`}
          />
        ))}

        {nonPhysicalLimitationsEnabled
          ? placementDetails.nonPhysicalLimitations?.map((limitation) => (
              <LimitationCard
                key={limitation.nonPhysicalLimitationTask.name}
                taskName={limitation.nonPhysicalLimitationTask.name}
                frequency={limitation.frequency}
                duration={limitation.duration}
                freeFormText={limitation.freeFormText}
                dataCy={`placement-details__limitations__task-${removeSpecialCharactersFromString(limitation.nonPhysicalLimitationTask.name)}`}
              />
            ))
          : null}
      </Column>
      <ConfirmUpdateRestrictionsModal
        open={isRestrictionEntriesErrorModalOpen}
        onClose={() => setIsRestrictionEntriesErrorModalOpen(false)}
        submit={updateRestrictions}
      />
    </>
  ) : null;
};

export default Restrictions;
